/**
 * Browser Notification API integration for critical/warning alerts.
 *
 * - Sends a desktop notification for critical alerts that arrive
 *   while the tab is hidden so we don't double-notify while the
 *   user is looking at the SPA.
 * - If the tab was visible when an alert landed but becomes hidden
 *   within a short window afterwards, we still fire a notification
 *   via a `visibilitychange` listener.
 * - The permission prompt is requested lazily, only when the tab is
 *   hidden (or becomes hidden) with a fresh critical alert queued.
 *   This avoids surprise-blocking operators who reflexively dismiss
 *   the prompt while actively driving the SPA, which would
 *   permanently disable desktop notifications for the origin and
 *   silently degrade the safety-critical alerting path.
 * - Clicking the notification navigates to `/alerts`, or does what the
 *   caller's `onActivate` says (a site audience goes to the Overview,
 *   where its toast would have taken it).
 * - ONE notification per alert id, whenever it arrives. The check re-runs
 *   whenever a new critical row arrives (amendment A9). It used to run
 *   only on mount and on a route change: the rows were kept in a ref and
 *   left out of the effect's dependencies, so a critical alert that
 *   arrived while the tab sat hidden on one screen raised nothing until
 *   somebody changed screens -- the one moment the OS notification
 *   exists for.
 * - The BACKLOG is never news. The first rows delivered under each
 *   `deliveryKey` (a page load, a site or user switch) are whatever was
 *   already open, however long ago: they are recorded as seen and raise
 *   nothing. Every open tab is reloaded after a deploy (the new-build
 *   banner's Reload, `shell/new-build.ts`), so without this a hidden
 *   console tab raised its site's whole open list as if each alert had
 *   just happened. Only a row that arrives AFTER that first delivery is
 *   news. The cost: a
 *   critical alert that opened in the instant before a load gets its
 *   toast and its bell row but no OS notification. Telling it from one
 *   opened hours earlier would mean comparing the server's `openedAtMs`
 *   with this device's clock, which contract §2 rules out.
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AlertViewModel } from '../api/adapters';

type PermissionState = 'default' | 'granted' | 'denied' | 'unsupported';

function getPermissionState(): PermissionState {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission as PermissionState;
}

async function ensurePermission(): Promise<PermissionState> {
  const cur = getPermissionState();
  if (cur === 'unsupported' || cur === 'granted' || cur === 'denied') return cur;
  try {
    const res = await Notification.requestPermission();
    return res as PermissionState;
  } catch {
    return 'denied';
  }
}

export interface BrowserAlertNotificationsOptions {
  /**
   * Which delivery of the caller's list `alerts` is: null until the list
   * has been delivered at all, then a key that changes whenever the caller
   * starts the list over (a site or user switch). The first rows seen
   * under each key are the backlog, and raise nothing (see the header).
   * Required: a caller that cannot say which rows are its backlog would
   * raise all of them.
   */
  deliveryKey: string | null;
  /** Skip the permission prompt even on the first critical alert.
   *  Used by tests + contexts where the host page opts out. */
  skipPermissionPrompt?: boolean;
  /** Maximum age (ms) for a critical alert to still be surfaced
   *  after the tab becomes hidden. Defaults to 5 s. */
  freshnessWindowMs?: number;
  /** What a click on the notification does, instead of going to
   *  `/alerts`. Called with the row the notification was raised for. */
  onActivate?: (alert: AlertViewModel) => void;
}

const DEFAULTS = {
  freshnessWindowMs: 5_000,
};

/**
 * The critical rows' ids, as one string: what the check re-runs on. A new
 * array with the same critical rows (every alerts-cache tick) changes
 * nothing; a new critical row, or a row turning critical, does.
 */
function criticalIdsKey(alerts: readonly AlertViewModel[]): string {
  return alerts
    .filter((a) => a.severity === 'critical')
    .map((a) => a.id)
    .join('\n');
}

/**
 * React hook: send a desktop notification for each newly seen
 * critical alert. `seenIdsRef` should be a ref the caller maintains
 * so we don't notify the same alert twice. The hook doesn't read
 * its own state — it just exposes the navigate function for the
 * click handler.
 */
export function useBrowserAlertNotifications(
  alerts: AlertViewModel[],
  options: BrowserAlertNotificationsOptions,
) {
  const navigate = useNavigate();
  const seenIdsRef = useRef<Set<string>>(new Set());
  const lastSeenAtRef = useRef<Map<string, number>>(new Map());
  // The delivery whose backlog has been recorded; null before any.
  const baselinedForRef = useRef<string | null>(null);
  const { deliveryKey } = options;

  // Keep the latest alerts in a ref so the visibility/permission
  // effect can stay mounted across alerts ticks instead of being
  // torn down + re-registered ~10×/sec when the caller passes a
  // fresh array reference. The effect re-runs on `criticalKey`
  // instead: only when the critical rows themselves change.
  const alertsRef = useRef<AlertViewModel[]>(alerts);
  useEffect(() => {
    alertsRef.current = alerts;
  }, [alerts]);
  const criticalKey = criticalIdsKey(alerts);

  // The click handler, read at click time, so a navigation (which gives
  // `navigate` a new identity) or a new `onActivate` never re-runs the
  // check below.
  const activateRef = useRef<(alert: AlertViewModel) => void>(() => navigate('/alerts'));
  const onActivate = options.onActivate;
  useEffect(() => {
    activateRef.current = onActivate ?? (() => navigate('/alerts'));
  }, [onActivate, navigate]);

  useEffect(() => {
    if (getPermissionState() === 'unsupported') return;
    // Nothing delivered yet: there is no backlog to tell news from.
    if (deliveryKey === null) return;
    if (baselinedForRef.current !== deliveryKey) {
      // The first rows of this delivery: already open, so not news.
      baselinedForRef.current = deliveryKey;
      for (const a of alertsRef.current) {
        if (a.severity === 'critical') seenIdsRef.current.add(a.id);
      }
      return;
    }

    const computeFresh = () =>
      alertsRef.current.filter(
        (a) => a.severity === 'critical' && !seenIdsRef.current.has(a.id),
      );

    const fresh = computeFresh();
    if (fresh.length === 0) return;

    // When each row was FIRST seen. Never re-stamped by a later run: a
    // row that arrived while the tab was visible, and was left there past
    // the window, must not raise a notification because some other row
    // arrived later.
    const now = Date.now();
    for (const a of fresh) {
      if (!lastSeenAtRef.current.has(a.id)) lastSeenAtRef.current.set(a.id, now);
    }

    let cancelled = false;
    const fireIfHidden = () => {
      if (cancelled) return;
      if (typeof document === 'undefined' || !document.hidden) return;
      void ensurePermission().then((state) => {
        if (cancelled || state !== 'granted') return;
        const cutoff = Date.now() - (options.freshnessWindowMs ?? DEFAULTS.freshnessWindowMs);
        for (const a of computeFresh()) {
          if (seenIdsRef.current.has(a.id)) continue;
          const last = lastSeenAtRef.current.get(a.id) ?? 0;
          if (last < cutoff) continue;
          seenIdsRef.current.add(a.id);
          try {
            const n = new Notification(`Critical alert: ${a.source || 'Alert'}`, {
              body: a.message || '—',
              tag: a.id,
              silent: false,
            });
            n.onclick = () => {
              try {
                window.focus();
              } catch {
                // ignore
              }
              activateRef.current(a);
              n.close();
            };
          } catch {
            // notification creation can fail if the browser denies
            // mid-session; ignore.
          }
        }
      });
    };

    // If we're already hidden, fire immediately. `fireIfHidden`
    // is the only path that calls `ensurePermission`, so we only
    // ask for the notification permission when the tab is hidden
    // (or becomes hidden). This avoids a surprise prompt popping up
    // while the operator is actively using the SPA — a reflex
    // "Block" click here would permanently disable desktop
    // notifications for this origin.
    fireIfHidden();
    // Otherwise, listen for a visibility change so a notification
    // still surfaces (and the prompt is requested) if the operator
    // switches tabs shortly after the alert arrived.
    const onVisibility = () => {
      if (document.hidden) fireIfHidden();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // Re-run when the critical rows change (A9), never on a plain tick;
    // ref reads keep everything else current without churning.
  }, [criticalKey, deliveryKey, options.skipPermissionPrompt, options.freshnessWindowMs]);
}